package com.schoolconnect.app.datasource.network

import com.schoolconnect.app.datasource.APP_STORAGE_PREFIX
import com.russhwolf.settings.Settings

internal object UserSessionStore {
    private const val USER_ID_KEY = "${APP_STORAGE_PREFIX}user_id"
    private const val HOME_ROLE_CODE_KEY = "${APP_STORAGE_PREFIX}home_role_code"
    private const val HOME_ROLE_SCHOOL_ID_KEY = "${APP_STORAGE_PREFIX}home_role_school_id"
    private const val HOME_ROLE_SCHOOL_CODE_KEY = "${APP_STORAGE_PREFIX}home_role_school_code"
    private const val ACTIVE_ASSIGNMENT_ID_KEY = "${APP_STORAGE_PREFIX}active_role_assignment_id"
    private const val PENDING_PUSH_TOKEN_KEY = "${APP_STORAGE_PREFIX}pending_push_token"
    private const val PENDING_PUSH_DEVICE_ID_KEY = "${APP_STORAGE_PREFIX}pending_push_device_id"
    private const val LAST_FETCHED_PREFIX = "${APP_STORAGE_PREFIX}last_fetched_id_"

    private val settingsOrNull: Settings? by lazy(LazyThreadSafetyMode.SYNCHRONIZED) {
        runCatching { Settings() }.getOrNull()
    }

    fun getUserId(): String = settingsOrNull?.getString(USER_ID_KEY, "").orEmpty()

    fun setUserId(userId: String) {
        settingsOrNull?.putString(USER_ID_KEY, userId)
    }

    fun getHomeRoleCode(): String = settingsOrNull?.getString(HOME_ROLE_CODE_KEY, "").orEmpty()

    fun setHomeRoleCode(roleCode: String) {
        val normalized = roleCode.trim()
        if (normalized.isBlank()) {
            settingsOrNull?.remove(HOME_ROLE_CODE_KEY)
        } else {
            settingsOrNull?.putString(HOME_ROLE_CODE_KEY, normalized)
        }
    }

    fun getHomeRoleSchoolId(): String =
        settingsOrNull?.getString(HOME_ROLE_SCHOOL_ID_KEY, "").orEmpty()

    fun setHomeRoleSchoolId(schoolId: String) {
        val normalized = schoolId.trim()
        if (normalized.isBlank()) {
            settingsOrNull?.remove(HOME_ROLE_SCHOOL_ID_KEY)
        } else {
            settingsOrNull?.putString(HOME_ROLE_SCHOOL_ID_KEY, normalized)
        }
    }

    fun getHomeRoleSchoolCode(): String =
        settingsOrNull?.getString(HOME_ROLE_SCHOOL_CODE_KEY, "").orEmpty()

    fun setHomeRoleSchoolCode(schoolCode: String) {
        val normalized = schoolCode.trim()
        if (normalized.isBlank()) {
            settingsOrNull?.remove(HOME_ROLE_SCHOOL_CODE_KEY)
        } else {
            settingsOrNull?.putString(HOME_ROLE_SCHOOL_CODE_KEY, normalized)
        }
    }

    fun getActiveAssignmentId(): String =
        settingsOrNull?.getString(ACTIVE_ASSIGNMENT_ID_KEY, "").orEmpty()

    fun setActiveAssignmentId(assignmentId: String) {
        val normalized = assignmentId.trim()
        if (normalized.isBlank()) {
            settingsOrNull?.remove(ACTIVE_ASSIGNMENT_ID_KEY)
        } else {
            settingsOrNull?.putString(ACTIVE_ASSIGNMENT_ID_KEY, normalized)
        }
    }

    fun clear() {
        TokenStore.clear()
        AuthSessionStore.clear()
        settingsOrNull?.remove(USER_ID_KEY)
        settingsOrNull?.remove(HOME_ROLE_CODE_KEY)
        settingsOrNull?.remove(HOME_ROLE_SCHOOL_ID_KEY)
        settingsOrNull?.remove(HOME_ROLE_SCHOOL_CODE_KEY)
        settingsOrNull?.remove(ACTIVE_ASSIGNMENT_ID_KEY)
        settingsOrNull?.remove(PENDING_PUSH_TOKEN_KEY)
        settingsOrNull?.remove(PENDING_PUSH_DEVICE_ID_KEY)
    }

    fun getPendingPushToken(): String =
        settingsOrNull?.getString(PENDING_PUSH_TOKEN_KEY, "").orEmpty()

    fun setPendingPushToken(token: String) {
        val normalized = token.trim()
        if (normalized.isBlank()) {
            settingsOrNull?.remove(PENDING_PUSH_TOKEN_KEY)
        } else {
            settingsOrNull?.putString(PENDING_PUSH_TOKEN_KEY, normalized)
        }
    }

    fun clearPendingPushToken() {
        settingsOrNull?.remove(PENDING_PUSH_TOKEN_KEY)
        settingsOrNull?.remove(PENDING_PUSH_DEVICE_ID_KEY)
    }

    fun getPendingPushDeviceId(): String =
        settingsOrNull?.getString(PENDING_PUSH_DEVICE_ID_KEY, "").orEmpty()

    fun setPendingPushDeviceId(deviceId: String) {
        val normalized = deviceId.trim()
        if (normalized.isBlank()) {
            settingsOrNull?.remove(PENDING_PUSH_DEVICE_ID_KEY)
        } else {
            settingsOrNull?.putString(PENDING_PUSH_DEVICE_ID_KEY, normalized)
        }
    }

    private fun lastFetchedKey(moduleKey: String, scopeKey: String): String =
        "$LAST_FETCHED_PREFIX${moduleKey.lowercase()}_${scopeKey.lowercase()}"

    fun getLastFetchedId(moduleKey: String, scopeKey: String = "global"): String =
        settingsOrNull?.getString(lastFetchedKey(moduleKey, scopeKey), "").orEmpty()

    fun setLastFetchedId(moduleKey: String, value: String, scopeKey: String = "global") {
        val key = lastFetchedKey(moduleKey, scopeKey)
        if (value.isBlank()) {
            settingsOrNull?.remove(key)
        } else {
            settingsOrNull?.putString(key, value)
        }
    }

    fun clearLastFetchedId(moduleKey: String, scopeKey: String = "global") {
        settingsOrNull?.remove(lastFetchedKey(moduleKey, scopeKey))
    }
}
